import { useRef } from "react";
import * as yup from 'yup';
import { Formik } from 'formik';
import { Button as BSButton, Form } from "react-bootstrap";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Popup, PopupProps } from "@progress/kendo-react-popup";
import { ScheduleType } from "../../../data/entities";
import { toYearMonthDayLocalString } from "../../../utils";
import {
  Button,
  FormSectionHeader,
  SlideOutModal,
  SlideOutModalProps,
} from "../../../components";

export interface AddScheduleModalProps extends SlideOutModalProps {
  onSubmit: any;
  providerId: number;
  scheduleType: ScheduleType;
  label: string;
}

export function AddScheduleModal(props: Readonly<AddScheduleModalProps>) {

  const submitRef = useRef<HTMLButtonElement>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const isException = props.scheduleType === ScheduleType.Override;

  const ModalPopup = (popupProps: PopupProps) => (
    <Popup {...popupProps} appendTo={modalBodyRef.current} />
  );

  const validationSchema = yup.object().shape({
    providerId: yup.number().required(),
    scheduleType: yup
      .mixed<ScheduleType>()
      .oneOf([ScheduleType.Default, ScheduleType.Override])
      .required(),
    effectiveDate: yup.date().required('Effective Date is required'),
    effectiveEndDate: yup.date().when('scheduleType', {
      is: ScheduleType.Override,
      then: (schema) => schema
        .required("Effective End Date is required")
        .min(
          yup.ref('effectiveDate'),
          "Effective End Date must be the same as or after Effective Date"
        ),
      otherwise: (schema) => schema.notRequired().nullable()
    })
  });

  return (
    <SlideOutModal
      {...props}
      title={`Add ${props.label}`}
      modalBodyRef={modalBodyRef}
      footer={
        <div className="container p-0">
          <div className="row g-0">
            <div className="col-12 pe-2">
              <Button onClick={() => props.showState.setter(false)} variant="cancel" className="w-100">
                Cancel
              </Button>
            </div>
            <div className="col-12 ps-2">
              <Button onClick={() => {
                submitRef.current?.click()
              }} variant="primary" className="w-100">
                Save
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={{
          providerId: props.providerId,
          scheduleType: props.scheduleType,
          effectiveDate: new Date() as Date | null,
          effectiveEndDate: (isException ? new Date() : null) as Date | null,
          scheduleBlocks: [],
        }}
        onSubmit={(values) => {
          // The API expects plain YYYY-MM-DD strings
          const schedule = {
            providerId: values.providerId,
            scheduleType: values.scheduleType,
            effectiveDate: values.effectiveDate ? toYearMonthDayLocalString(values.effectiveDate) : null,
            effectiveEndDate: values.effectiveEndDate ? toYearMonthDayLocalString(values.effectiveEndDate) : null,
            scheduleBlocks: values.scheduleBlocks
          }
          props.onSubmit(schedule);
        }}
      >
        {({ handleSubmit, errors, values, touched, setFieldValue }) => {
          return (
            <Form onSubmit={handleSubmit} className="px-2 pt-2">

              <FormSectionHeader>
                {isException ? "Exception Period" : "Schedule Start"}
              </FormSectionHeader>
              <p className="small text-body-secondary mb-3">
                {isException
                  ? "Temporarily overrides the regular schedule between these dates."
                  : "The new schedule takes effect on this date and stays in effect until another schedule replaces it."}
              </p>

              <Form.Group controlId="effectiveDate" className="mb-3">
                <Form.Label className="small text-body-secondary mb-1">
                  {isException ? "Start Date" : "Effective Date"}
                </Form.Label>
                <DatePicker
                  value={values.effectiveDate}
                  onChange={(e) => setFieldValue("effectiveDate", e.value)}
                  popup={ModalPopup}
                  className="w-100"
                />
                {touched.effectiveDate && typeof errors.effectiveDate === "string" && (
                  <div className="text-danger mt-1 small">{errors.effectiveDate}</div>
                )}
              </Form.Group>

              {isException && (
                <Form.Group controlId="effectiveEndDate" className="mb-3">
                  <Form.Label className="small text-body-secondary mb-1">End Date</Form.Label>
                  <DatePicker
                    value={values.effectiveEndDate}
                    onChange={(e) => setFieldValue("effectiveEndDate", e.value)}
                    popup={ModalPopup}
                    className="w-100"
                    min={values.effectiveDate || undefined}
                  />
                  {touched.effectiveEndDate && typeof errors.effectiveEndDate === "string" && (
                    <div className="text-danger mt-1 small">{errors.effectiveEndDate}</div>
                  )}
                </Form.Group>
              )}

              <BSButton ref={submitRef} type="submit" className="d-none" />
            </Form>
          )
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default AddScheduleModal;
